<template>
  <div class="category-filter" :class="classModifiers">
    <sp-quantum-select
      :aria-label="label"
      class="category-filter__select"
      navigation="flat"
      :multiple="isMultiple"
      :checkboxes="isMultiple"
      :close-on-select="!isMultiple"
      clearable
      show-search-bar-in-sheet
      :title="label"
      :items.prop="models"
      :selected.prop="selectedIds"
      :location="location"
      :open="open"
      :required="isNonClearable"
      :pointer="showPointer"
      :placeholder="t('finder_filter_search_placeholder')"
      :no-data-text="t('finder_filter_no_results')"
      :input-test-id="TestId.events.categoryFilterSearch"
      :item-test-id="TestId.events.categoryFilterOption"
      @update-selected="handleUpdateSelected"
      @update-open="handleUpdateOpen"
    >
      <FilterActivatorSlot
        :label="label"
        :text="selectedText"
        :prepend-icon="prependIcon"
        :append-icon="appendIcon"
        :open="open"
        :non-clearable="isNonClearable"
        @clear="clear"
      >
        <template v-if="$slots.activator" #activator="activatorProps">
          <slot name="activator" v-bind="activatorProps" />
        </template>
      </FilterActivatorSlot>

      <sp-button
        v-if="showClear"
        slot="navbar-actions"
        class="category-filter__clear"
        variant="text-inline"
        color="primary"
        size="small"
        :data-testid="TestId.events.categoryFilterClear"
        @click="clear"
        >{{ t("finder_filter_clear") }}</sp-button
      >
    </sp-quantum-select>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { toBoolean, toJSON } from "#ce/utils/props";
import { firstDetail } from "#ce/utils/events";
import { TestId } from "#/test-ids";
import { useForm } from "#ce/composables/useForm";
import { useControllableOpen } from "#ce/shared/filters/composables/useControllableOpen";
import { t } from "#ce/shared/components-i18n";
import { props as categoryFilterProps } from "#ce/shared/filters/category/components/props";
import FilterActivatorSlot from "#ce/shared/filters/components/FilterActivatorSlot.ce.vue";

const emit = defineEmits(["update:model-value", "update:open"]);

const props = defineProps(categoryFilterProps);

const isMultiple = computed(() => !toBoolean(props.single));

// Ids are held as strings, the form the URL param carries and the row values compare in.
function toIds(value) {
  const ids = typeof value === "string" ? toJSON(value, true) : value;

  return (Array.isArray(ids) ? ids : []).map(String).filter(Boolean);
}

// Returns the ids the selection holds: all of them, or the first alone under `single`.
function parseIds(value) {
  const ids = toIds(value);

  return isMultiple.value ? ids : ids.slice(0, 1);
}

// Reports a selection the filter narrowed on its own, so the host's model matches what the filter
// holds. `seeded` tells the host to move its model without dispatching a change.
function reportNarrowedSelection() {
  emit("update:model-value", selectedIds.value.length ? [...selectedIds.value] : undefined, { seeded: true });
}

function areIdsEqual(a, b) {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

// Ungrouped rows lead the list as one headerless block. `sp-quantum-select` keeps ungrouped rows
// ahead of the first grouped row in place, and buckets any that trail a group alphabetically.
const models = computed(() => {
  const items = typeof props.items === "string" ? toJSON(props.items, true) : props.items;
  const rows = (Array.isArray(items) ? items : []).map((item) => {
    const row = { title: item.name, value: String(item.id) };

    if (item.group != null) {
      row.group = item.group;
    }

    return row;
  });

  return [...rows.filter((row) => !("group" in row)), ...rows.filter((row) => "group" in row)];
});

const selectedIds = ref(parseIds(props.modelValue));

// A prop sync mirrors silently; only a user's pick or clear reports a change upward. A seed
// carrying more ids than `single` holds is the exception: the narrowing is reported, so
// `host.value` reads what the filter holds.
function syncSelectedIdsFromModelValue(value) {
  const next = parseIds(value);

  if (!areIdsEqual(next, selectedIds.value)) {
    selectedIds.value = next;
  }

  if (next.length < toIds(value).length) {
    reportNarrowedSelection();
  }
}

// `single` caps how many ids the selection may hold, so tightening it trims the live selection.
// `modelValue` can lag a pick the parent has not mirrored back yet, so it is not re-read here.
function trimSelectionToSingle() {
  if (isMultiple.value || selectedIds.value.length <= 1) {
    return;
  }

  selectedIds.value = selectedIds.value.slice(0, 1);
  reportNarrowedSelection();
}

watch(() => props.modelValue, syncSelectedIdsFromModelValue, { immediate: true });
watch(() => props.single, trimSelectionToSingle);

const hasSelection = computed(() => selectedIds.value.length > 0);
const isNonClearable = computed(() => toBoolean(props.nonClearable));
const canClear = computed(() => !isNonClearable.value);
const showPointer = computed(() => !toBoolean(props.hidePointer));
const showClear = computed(() => hasSelection.value && canClear.value);

/** The activator text: the one selected category's name, or the count while several are selected. */
const selectedText = computed(() => {
  const count = selectedIds.value.length;

  if (count === 0) {
    return undefined;
  }
  if (count === 1) {
    return models.value.find((row) => row.value === selectedIds.value[0])?.title ?? selectedIds.value[0];
  }

  return t("finder_filter_multiple_selected", { count });
});

// A cleared filter reports `undefined`, which the host reads as inactive.
function emitModelValue() {
  emit("update:model-value", hasSelection.value ? [...selectedIds.value] : undefined);
}

// The host dispatches the one change per user update; each selected id is submitted as its own
// entry under `name`, the way a native <select multiple> submits.
const { mirrorFormValue } = useForm();

mirrorFormValue(
  computed(() => {
    const data = new FormData();
    selectedIds.value.forEach((id) => data.append(props.name, id));

    return data;
  }),
);

const open = useControllableOpen(() => props.open, emit);

function handleUpdateSelected(event) {
  const next = parseIds(firstDetail(event) ?? []);

  if (!areIdsEqual(next, selectedIds.value)) {
    selectedIds.value = next;
    emitModelValue();
  }
}

function handleUpdateOpen(event) {
  open.value = firstDetail(event);
}

function clear() {
  if (!hasSelection.value || !canClear.value) {
    return;
  }

  selectedIds.value = [];
  emitModelValue();
}

const classModifiers = computed(() => ({
  "--has-selected-model": hasSelection.value,
}));

defineExpose({ props, active: hasSelection, selectedText });
</script>

<style>
:host {
  --filter-width: 100%;
  display: block;
  box-sizing: border-box;
}
:host([chrome="bar"]) {
  --filter-width: max-content;
  --sp-comp-popup-width: var(--sp-comp-filter-bar-popup-width, 18.5rem);
}
</style>

<style lang="scss" scoped>
.category-filter {
  --sp-comp-button-width: var(--filter-width);

  display: flex;
  align-items: center;
  height: 100%;
  width: var(--filter-width);

  &__select {
    height: 100%;
    width: var(--filter-width);
  }

  // The button host is a block; flex centres its inline button in the title bar.
  &__clear {
    display: flex;
  }

  .activator {
    width: var(--filter-width);
  }

  &.--has-selected-model .activator {
    --sp-comp-text-field-color: var(--sp-sys-color-on-surface);
  }
}
</style>
